// frontend/src/hooks/usePromotionDraft.js
//
// Draft state for the create/edit-promotion dialog (PromotionForm).
//
// Reads and writes through `sessionApi`, which does not throw on non-2xx: the
// hook branches on res.ok / res.status and parses the JSON body itself, which
// is how the backend's 409 price_confirmation_required and overlap
// `conflicts` payloads are read.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { useFormatters } from './useFormatters';
import { formatApiError, apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { DATA, useDataChanged } from '../queries/dataChanged';
import { useItemSuggestions } from '../features/item-filter';
import { useAuth } from '../contexts/AuthContext';
import { percentOffPrice, roundMoney } from '../components/promotions/promoPrice';

const PROMOTIONS_API = '/api/promotions/';
// Member set is full-replaced on save via items_write; loading a truncated set
// would silently drop the un-loaded members — so a short page BLOCKS the save
// (`membersTruncated`) rather than quietly saving what it has.
//
// This is the server's own maximum for the endpoint
// (api/views/promotions.py PromotionItemPagination.max_page_size), restated
// here because the request has to name a number. `StandardPagination` refuses
// an over-cap `page_size` with a 400, so a lower server bound surfaces here as
// a failed load.
const MEMBERS_PAGE_SIZE = 2000;

// -----------------------------------------------------------------------------
// Arguments
// -----------------------------------------------------------------------------
//   open            — bool, drives the reset / edit-populate effects
//   editPromotion   — { PromotionID, status, ... } or null (create mode)
//   onSaved         — (savedPromotion) => void, after a successful save; the
//                     body names the items left at their regular price
//                     (`not_started`) when there are any
//   onClose         — () => void
//
// A landed save is announced here (`queries/dataChanged`): the promotion
// changed, its lift was re-applied to the forecast rows and, when it went
// live, the items' sell prices moved.
// -----------------------------------------------------------------------------

// '' → null (not entered); otherwise `parseNumber` — NaN when the text
// is not a number. Callers decide whether null is allowed; NaN never is.
function parseSignedField(raw, parseNumber) {
  if (raw === '') return null;
  const n = parseNumber(raw);
  return Number.isFinite(n) ? n : NaN;
}

// A whole-number discount, as stores write them, and the backend's own
// bounds (Promotion.DISCOUNT_MIN / DISCOUNT_MAX): 0 % is no discount, 100 %
// gives the item away.
const DISCOUNT_MIN = 1;
const DISCOUNT_MAX = 99;

// A row's price can only be discounted from a positive current price — the
// backend skips the rest (promotion_engine._apply_discount_percentage).
const hasCurrentPrice = (gi) => gi.current_sell_price != null && Number(gi.current_sell_price) > 0;

export function usePromotionDraft({
  open,
  editPromotion = null,
  onSaved,
  onClose,
}) {
  const { t } = useTranslation();
  const { formatNumber, parseNumber } = useFormatters();
  const changed = useDataChanged();
  const { selectedStore } = useAuth();
  const currencyDigits = selectedStore.currency_digits;

  // --- Form state ---
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [liftType, setLiftType] = useState('percentage');
  const [liftValue, setLiftValue] = useState('0');
  const [preAdjustment, setPreAdjustment] = useState('');
  const [postAdjustment, setPostAdjustment] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  // How promo prices are set: 'percent' — one discount prices every row,
  // computed here and by the backend at activation, rows carry no price of
  // their own; 'individual' — each row's typed price, no discount.
  const [pricingMode, setPricingMode] = useState('percent');
  const [selectionMode, setSelectionMode] = useState('individual');
  // The editable member rows, in ONE shape whichever of the three sources
  // filled them (saved members, a supplier/category sweep, the search picker):
  // { item_id, item_code, item_name, image_url, sku, current_sell_price,
  //   promotional_sell_price, lift_override }.
  const [gridItems, setGridItems] = useState([]);
  // True when an existing promotion has MORE members than we loaded into the
  // editable grid (the MEMBERS_PAGE_SIZE cap). Saving is a full-replace via
  // items_write, so saving a truncated set would silently drop the members we
  // never loaded — block the save instead (fail loud, never lose data).
  const [membersTruncated, setMembersTruncated] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  // Full ProductCategory object (or null). Picker round-trips the whole row.
  const [selectedProductCategory, setSelectedProductCategory] = useState(null);

  // --- Item search (individual mode) ---
  const [itemSearchText, setItemSearchText] = useState('');
  const itemSuggestions = useItemSuggestions(itemSearchText);

  // --- Submit state ---
  // The status the running save was asked for ('draft' | 'active'), null when
  // idle: the form spins the one button the merchant pressed.
  const [savingAs, setSavingAs] = useState(null);
  const saving = savingAs !== null;
  const [error, setError] = useState(null);
  // Structured overlap conflicts from the backend (promotion_engine
  // check_item_overlap). Non-empty → rendered as a structured list with
  // per-row highlighting in the item grid.
  const [conflicts, setConflicts] = useState([]);
  // Blast-radius confirmation for a large price promo: { targetStatus, count }
  // set when the backend returns 409 price_confirmation_required.
  const [confirmPrice, setConfirmPrice] = useState(null);
  // Outcome of the async bulk price push/revert (backend `price_sync`), so the
  // merchant sees the result of a real-money change — null when unused.
  const [priceSync, setPriceSync] = useState(null);
  // A save refused on screen because rows would not get a promo price: the
  // popup that says so, and the row highlight that stays once it is answered.
  const [priceCheckOpen, setPriceCheckOpen] = useState(false);
  const [highlightPriceIssues, setHighlightPriceIssues] = useState(false);
  // Switching to the percentage would replace prices the merchant typed.
  const [confirmPercentSwitch, setConfirmPercentSwitch] = useState(false);

  // ---------------------------------------------------------------------------
  const resetForm = useCallback(() => {
    setName(''); setStartDate(''); setEndDate(''); setNotes('');
    setLiftType('percentage'); setLiftValue('0');
    setPreAdjustment(''); setPostAdjustment('');
    setDiscountPercentage(''); setPricingMode('percent');
    setSelectionMode('individual'); setGridItems([]);
    setSelectedSupplier(null); setSelectedProductCategory(null);
    setError(null);
    setConflicts([]);
    setMembersTruncated(false);
    setPriceSync(null);
    setPriceCheckOpen(false); setHighlightPriceIssues(false);
    setConfirmPercentSwitch(false);
  }, []);

  // Load an existing promotion's saved members into the editable grid. Members
  // are NOT embedded in the promotion detail (an 'all items' promo spans the
  // whole catalogue); they're paged from the dedicated endpoint,
  // capped at MEMBERS_PAGE_SIZE. Only bounded modes call this — 'all' restores
  // its mode without enumerating (see the edit effect).
  //
  // The rows also decide the pricing mode, set with them in one render: a
  // saved discount is percentage pricing; without one, rows that carry their
  // own prices are individual pricing, and rows without any are a promotion
  // that changes no price — percentage pricing with no discount.
  const loadMembers = useCallback(async (promo) => {
    try {
      const res = await sessionApi.get(
        `/api/promotion-items/?promotion=${promo.PromotionID}&page_size=${MEMBERS_PAGE_SIZE}`,
      );
      if (!res.ok) throw await apiErrorFrom(res, 'GET /api/promotion-items/');
      const data = await res.json();
      const { results } = data;
      const truncated = data.count > results.length;
      setMembersTruncated(truncated);
      if (truncated) {
        // Full-replace save would drop the un-loaded members — isValid blocks
        // the save until the merchant narrows/rebuilds the selection.
        setError(t('promotions.tooManyItems', { count: data.count }));
      }
      const rows = results.map(pi => ({
        item_id: pi.item,
        item_code: pi.item_code,
        item_name: pi.item_name,
        image_url: pi.image_url,
        sku: pi.sku,
        current_sell_price: pi.current_sell_price,
        // Rounded like a typed price, so the checks and the save see the
        // price the store will get.
        promotional_sell_price: pi.promotional_sell_price == null
          ? null : roundMoney(pi.promotional_sell_price, currencyDigits),
        lift_override: pi.lift_override,
      }));
      setGridItems(rows);
      setPricingMode(
        promo.discount_percentage == null && rows.some(r => r.promotional_sell_price != null)
          ? 'individual' : 'percent',
      );
    } catch (err) {
      console.error('Promotion members load failed:', err.message);
      setError(translateRequestError(err, t, 'common.fetchError'));
    }
  }, [t, currencyDigits]);

  // The tools the populate effect uses, latched so they are not TRIGGERS for
  // it. What the dialog is editing (`open`, `editPromotion`) decides when the
  // form is (re)built; a new `t` (a language switch) must not, or it would
  // reset the form over the merchant's typing.
  const tools = useRef({ t, formatNumber, loadMembers, resetForm });
  // Refreshed after every render (no dep array), and declared BEFORE the effect
  // that reads it: effects run in declaration order, so the populate effect
  // always sees this commit's tools, never the previous render's.
  useEffect(() => {
    tools.current = { t, formatNumber, loadMembers, resetForm };
  });

  // Populate form when editing / reset when creating
  useEffect(() => {
    // Shadows the props deliberately — same values, read through the latch so
    // eslint sees no stale closure and the dep list stays honest.
    const { t, formatNumber, loadMembers, resetForm } = tools.current;
    if (!open) return;
    if (!editPromotion) { resetForm(); return; }

    const populateForm = (promo) => {
      setPriceSync(promo.price_sync || null);
      setName(promo.name || '');
      setStartDate(promo.start_date || '');
      setEndDate(promo.end_date || '');
      setNotes(promo.notes || '');
      setLiftType(promo.lift_type || 'percentage');
      setLiftValue(promo.lift_value != null ? formatNumber(promo.lift_value, 2) : '');
      setPreAdjustment(promo.pre_adjustment_pct != null ? formatNumber(promo.pre_adjustment_pct, 1) : '');
      setPostAdjustment(promo.post_adjustment_pct != null ? formatNumber(promo.post_adjustment_pct, 1) : '');
      // Whole as stored ('40.00' → 40); a fraction saved before discounts were
      // whole shows as it is, under the field's error, never rounded away.
      setDiscountPercentage(promo.discount_percentage == null ? '' : formatNumber(
        promo.discount_percentage,
        Number.isInteger(Number(promo.discount_percentage)) ? 0 : 2,
      ));
      setPricingMode('percent');
      // 'all items' promos span the whole catalogue — restore the mode WITHOUT
      // enumerating members. Every other mode is bounded (≤ cap), so load its
      // members into the editable grid as the individual working set, which
      // saves as a full-replace via items_write.
      if (promo.item_selection_mode === 'all') {
        setSelectionMode('all');
        setGridItems([]);
      } else {
        setSelectionMode('individual');
        loadMembers(promo);
      }
    };

    // A failed detail read leaves the form UNPOPULATED and says so: the list
    // row (PromotionListSerializer) lacks the adjustment percentages, notes and
    // `item_selection_mode`, so a form built from it would save blanks over
    // the merchant's values.
    sessionApi.get(`${PROMOTIONS_API}${editPromotion.PromotionID}/`).then(async res => {
      if (!res.ok) throw await apiErrorFrom(res, `GET ${PROMOTIONS_API}<id>/`);
      populateForm(await res.json());
    }).catch(e => {
      console.error('Promotion detail load failed:', e.message);
      setError(translateRequestError(e, t, 'common.fetchError'));
    });
  }, [editPromotion, open]);

  // Load items for supplier / product_category / all selection modes.
  const loadItemsForMode = useCallback(async (mode, supplierId, groupId) => {
    // A fresh selection replaces the whole grid — any prior "truncated existing
    // members" block no longer applies.
    setMembersTruncated(false);
    const params = new URLSearchParams({ status: 'active' });
    if (mode === 'all') {
      // no extra filter
    } else if (mode === 'supplier' && supplierId) {
      params.set('supplier', supplierId);
    } else if (mode === 'product_category' && groupId) {
      params.set('product_category_id', groupId);
    } else {
      return;
    }
    try {
      const res = await sessionApi.get(`/api/items/compact/?${params}`);
      if (!res.ok) throw await apiErrorFrom(res, 'GET /api/items/compact/');
      const data = await res.json();
      if (data.truncated) {
        setError(t('promotions.tooManyItems', { count: data.count }));
      }
      setGridItems(data.results.map(item => ({
        item_id: item.item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        image_url: item.image_url,
        sku: item.sku,
        current_sell_price: item.current_sell_price,
        promotional_sell_price: null,
        lift_override: null,
      })));
    } catch (err) {
      console.error('Promotion item-mode load failed:', err.message);
      setError(translateRequestError(err, t, 'common.fetchError'));
    }
  }, [t]);

  // Switch selection mode. Only 'all' clears the grid (it sweeps server-side,
  // so the truncated-members block no longer applies). Every other mode keeps
  // the current working set; a fresh supplier/category pick replaces it via
  // loadItemsForMode.
  const changeSelectionMode = useCallback((mode) => {
    if (!mode) return;
    setSelectionMode(mode);
    if (mode === 'all') {
      setGridItems([]);
      setMembersTruncated(false);
      // No rows to type prices into: the whole catalogue takes the discount.
      setPricingMode('percent');
    }
  }, []);

  // Add item from the search picker to the grid. `item` is a raw `/api/items/`
  // row (ItemSerializer), which names the SKU `shopify_sku` — the other two
  // sources hand it over as `sku`, so it is normalized here to the one shape
  // the row identity is read in.
  const addItem = useCallback((item) => {
    setGridItems(prev => {
      if (prev.some(gi => gi.item_id === item.item_id)) return prev;
      return [...prev, {
        item_id: item.item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        image_url: item.image_url,
        sku: item.shopify_sku,
        current_sell_price: item.current_sell_price,
        promotional_sell_price: null,
        lift_override: null,
      }];
    });
    setItemSearchText('');
  }, []);

  const removeItem = useCallback((itemId) => {
    setGridItems(prev => prev.filter(gi => gi.item_id !== itemId));
  }, []);

  // A price typed into one row (individual pricing), rounded to the currency
  // so the row shows — and is checked against its current price as — what
  // the store will get. `null` clears it.
  const setPromoPrice = useCallback((itemId, price) => {
    const rounded = price == null ? null : roundMoney(price, currencyDigits);
    setGridItems(prev => prev.map(gi => (
      gi.item_id === itemId ? { ...gi, promotional_sell_price: rounded } : gi
    )));
  }, [currencyDigits]);

  // Single source of truth for "what makes this promotion saveable": isValid
  // derives from this list, and the form reads the numeric tokens back to
  // name the field a value was refused in. Rows without a real promo price
  // are not here — they stop the save with a popup (`priceRows`).
  const missingRequirements = [];
  if (!name.trim()) missingRequirements.push('name');
  if (!startDate) missingRequirements.push('startDate');
  if (!endDate) missingRequirements.push('endDate');
  // Numeric fields are gated HERE, never coerced at save time. The demand
  // change is required; the three others are optional but, once typed, must
  // parse.
  const liftNumber = parseSignedField(liftValue, parseNumber);
  if (liftNumber === null || Number.isNaN(liftNumber)) missingRequirements.push('liftValue');
  const preNumber = parseSignedField(preAdjustment, parseNumber);
  if (Number.isNaN(preNumber)) missingRequirements.push('preAdjustment');
  const postNumber = parseSignedField(postAdjustment, parseNumber);
  if (Number.isNaN(postNumber)) missingRequirements.push('postAdjustment');
  // The typed discount prices the rows; it is saved — and checked — only in
  // percentage pricing. Individual pricing saves none, but still reads it to
  // carry its prices over and to tell whether switching back replaces any.
  const typedDiscount = parseSignedField(discountPercentage, parseNumber);
  let discountProblem = null;
  if (Number.isNaN(typedDiscount)) {
    discountProblem = 'discountPercentage';
  } else if (typedDiscount !== null && !(
    Number.isInteger(typedDiscount)
    && typedDiscount >= DISCOUNT_MIN && typedDiscount <= DISCOUNT_MAX
  )) {
    discountProblem = 'discountRange';
  }
  const discountNumber = pricingMode === 'percent' ? typedDiscount : null;
  if (pricingMode === 'percent' && discountProblem) missingRequirements.push(discountProblem);
  const discountUsable = typedDiscount !== null && discountProblem === null;
  if (membersTruncated) {
    // Never save a full-replace over a member set we only partially loaded.
    missingRequirements.push('membersTruncated');
  } else if (!(
    selectionMode === 'all' ||
    gridItems.length > 0 ||
    (selectionMode === 'supplier' && selectedSupplier) ||
    (selectionMode === 'product_category' && selectedProductCategory)
  )) {
    missingRequirements.push('selection');
  }
  const isValid = missingRequirements.length === 0;

  // The rows as the grid shows them: in percentage pricing each row's price
  // is the discount applied to its current price; in individual pricing the
  // typed one. `null` = the price stays as it is. `discount_pct` is what the
  // price takes off; `price_issue` names why a row would not get a real promo
  // price — the save stops on any of them.
  const percentPriceOf = useCallback((gi) => (
    discountUsable && hasCurrentPrice(gi)
      ? percentOffPrice(gi.current_sell_price, typedDiscount, currencyDigits)
      : null
  ), [discountUsable, typedDiscount, currencyDigits]);
  const priceRows = useMemo(() => gridItems.map(gi => {
    const promo = pricingMode === 'percent' ? percentPriceOf(gi) : gi.promotional_sell_price;
    let priceIssue = null;
    if ((pricingMode === 'individual' || discountUsable) && !hasCurrentPrice(gi)) {
      priceIssue = 'noPrice';
    } else if (pricingMode === 'individual') {
      const current = Number(gi.current_sell_price);
      if (promo == null || Number(promo) === current) priceIssue = 'unchanged';
      else if (Number(promo) > current) priceIssue = 'aboveCurrent';
    }
    const discountPct = promo != null && hasCurrentPrice(gi)
      ? (1 - Number(promo) / Number(gi.current_sell_price)) * 100 : null;
    return { ...gi, promotional_sell_price: promo, discount_pct: discountPct, price_issue: priceIssue };
  }), [gridItems, pricingMode, discountUsable, percentPriceOf]);
  const priceIssueCounts = useMemo(() => {
    const counts = { unchanged: 0, aboveCurrent: 0, noPrice: 0 };
    for (const row of priceRows) if (row.price_issue) counts[row.price_issue] += 1;
    return counts;
  }, [priceRows]);
  const hasPriceIssues = priceRows.some(row => row.price_issue);

  // Percentage → individual: the computed prices become the starting values.
  // Individual → percentage: asks first when a typed price would be replaced.
  const switchToPercent = useCallback(() => {
    setGridItems(prev => prev.map(gi => ({ ...gi, promotional_sell_price: null })));
    setPricingMode('percent');
    setConfirmPercentSwitch(false);
  }, []);
  const changePricingMode = useCallback((mode) => {
    if (!mode || mode === pricingMode) return;
    if (mode === 'individual') {
      setGridItems(prev => prev.map(gi => ({ ...gi, promotional_sell_price: percentPriceOf(gi) })));
      setPricingMode('individual');
      return;
    }
    const replacesTyped = gridItems.some(gi => (
      gi.promotional_sell_price != null
      && Number(gi.promotional_sell_price) !== percentPriceOf(gi)
    ));
    if (replacesTyped) setConfirmPercentSwitch(true);
    else switchToPercent();
  }, [pricingMode, gridItems, percentPriceOf, switchToPercent]);

  const removePriceIssueItems = useCallback(() => {
    const flagged = new Set(priceRows.filter(row => row.price_issue).map(row => row.item_id));
    setGridItems(prev => prev.filter(gi => !flagged.has(gi.item_id)));
    setPriceCheckOpen(false);
  }, [priceRows]);

  // Set of ItemCodes that appear in any overlap conflict — the grid row class
  // matches rows against it to tint them. The item code is the MATCHING key
  // only; what the banner PRINTS is each entry's `label` (product name + SKU),
  // because a promotion is Shopify-only and the code there is a variant-id
  // suffix. Both travel on every overlap entry (find_item_overlaps).
  const conflictingItemCodes = useMemo(() => {
    const s = new Set();
    for (const c of conflicts) {
      for (const entry of (c.overlap_items || [])) s.add(entry.item_code);
    }
    return s;
  }, [conflicts]);

  const dismissError = useCallback(() => {
    setError(null);
    setConflicts([]);
  }, []);

  const save = useCallback(async (targetStatus, { confirmed = false } = {}) => {
    if (hasPriceIssues) {
      setHighlightPriceIssues(true);
      setPriceCheckOpen(true);
      return;
    }
    setSavingAs(targetStatus);
    setError(null);
    setConflicts([]);

    const payload = {
      name,
      start_date: startDate,
      end_date: endDate,
      lift_type: liftType,
      lift_value: liftNumber,
      pre_adjustment_pct: preNumber,
      post_adjustment_pct: postNumber,
      discount_percentage: discountNumber,
      notes,
      status: targetStatus,
      item_selection_mode: selectionMode,
    };
    // Acknowledgement that this large price promo may change prices on many
    // items — set once the merchant confirms the blast-radius prompt.
    if (confirmed) payload.confirmed = true;

    // Always send items from the grid (all bounded modes populate it). Only a
    // truly-empty supplier/category selection falls through to the rule-based
    // path; 'all' carries no rows (server-resolved membership).
    if (gridItems.length > 0) {
      payload.item_selection_mode = 'individual';
      // Percentage pricing sends no row prices: the backend applies the
      // discount to the price an item has when the promotion starts.
      payload.items_write = gridItems.map(gi => ({
        item_id: gi.item_id,
        lift_override: gi.lift_override,
        promotional_sell_price: pricingMode === 'individual' ? gi.promotional_sell_price : null,
      }));
    } else if (selectionMode === 'supplier') {
      payload.supplier_id = selectedSupplier;
    } else if (selectionMode === 'product_category') {
      payload.product_category_id = selectedProductCategory?.id || null;
    }

    try {
      const id = editPromotion?.PromotionID;
      const res = id
        ? await sessionApi.patch(`${PROMOTIONS_API}${id}/`, payload)
        : await sessionApi.post(PROMOTIONS_API, payload);

      if (res.ok) {
        const data = await res.json();
        changed([DATA.promotions, DATA.forecasts, DATA.items]);
        onSaved(data);
        onClose();
        return;
      }

      const err = await apiErrorFrom(res);
      if (err.code === 'price_confirmation_required') {
        // Large all-items (or large supplier/category) price promo — the
        // backend gates it behind an explicit confirmation. Surface the blast
        // radius; on confirm we re-submit the same status with confirmed:true.
        setConfirmPrice({
          targetStatus,
          count: err.body.member_item_count ?? null,
        });
        return;
      }
      // A coded refusal (a revert the channel refused, a gate) reads as its
      // own sentence (`apiErrors.<code>`), never as the server's raw body.
      if (err.code) {
        setError(translateRequestError(err, t, 'msg.saveFailed'));
        return;
      }
      // `conflicts` is structured overlap data (promo name + date range + item
      // codes per conflict). `items` is the narrative overlap message; when we
      // already have structured `conflicts`, suppress the text to avoid
      // duplicating the same information. Anything else (DRF field errors,
      // `detail`, `non_field_errors`) is flattened normally.
      const { conflicts: errConflicts, items, ...rest } = err.body ?? {};
      const hasStructured = Array.isArray(errConflicts) && errConflicts.length > 0;
      setConflicts(hasStructured ? errConflicts : []);
      const narrative = !hasStructured && typeof items === 'string' ? items : '';
      const secondary = formatApiError(rest, { maxLength: 300 });
      const combined = [narrative, secondary].filter(Boolean).join(' · ');
      setError(combined ? combined.slice(0, 300) : t('msg.saveFailed'));
    } catch (err) {
      console.error('Promotion save network failure:', err.message);
      setError(t('msg.saveFailed'));
    } finally {
      setSavingAs(null);
    }
  }, [name, startDate, endDate, liftType, liftNumber, preNumber, postNumber,
      discountNumber, notes, selectionMode, gridItems, selectedSupplier,
      selectedProductCategory, editPromotion, onSaved, changed, onClose,
      t, hasPriceIssues, pricingMode]);

  return {
    state: {
      name, startDate, endDate, notes,
      liftType, liftValue, preAdjustment, postAdjustment, discountPercentage,
      pricingMode, selectionMode, gridItems,
      selectedSupplier, selectedProductCategory,
      itemSearchText, itemSuggestions,
      saving, savingAs, error, conflicts, confirmPrice, priceSync,
      priceCheckOpen, highlightPriceIssues, confirmPercentSwitch,
    },
    actions: {
      setName, setStartDate, setEndDate, setNotes,
      setLiftType, setLiftValue, setPreAdjustment, setPostAdjustment,
      setDiscountPercentage,
      setSelectedSupplier, setSelectedProductCategory,
      setItemSearchText, setConfirmPrice,
      changeSelectionMode,
      loadItemsForMode,
      addItem, removeItem, setPromoPrice,
      changePricingMode, switchToPercent, setConfirmPercentSwitch,
      setPriceCheckOpen, removePriceIssueItems,
      dismissError,
      save,
    },
    derived: {
      isValid,
      missingRequirements,
      conflictingItemCodes,
      priceRows,
      priceIssueCounts,
      currencyDigits,
    },
  };
}

